/**
 * A QueueItem is one to-do that needs a human decision. The queue is not an
 * archive — items disappear the instant their action is taken.
 *
 * `type` is the discriminant. New action kinds (split_candidate,
 * event_detected, signing_needed, translation_needed, ...) join the union.
 * Everything else in this module keys off `type`, so adding a kind means
 * a new interface here + a new fetch branch in listQueueItems() + a new
 * renderer/resolver in QueueWidget.
 *
 * A single document can produce more than one queue item — a doc with an
 * unresolved AI name AND unresolved tag suggestions shows up as two rows
 * (one name_pending, one tags_pending). That's intentional: they're
 * independent decisions and the filter chip should be able to isolate one.
 */
export type QueueItem =
  | NamePendingItem
  | TagsPendingItem
  | SplitCandidateItem
  | EventPendingItem
  | InvitationPendingItem;

/** Fields every queue item has, regardless of what it's about. */
interface QueueItemBase {
  /** Stable per (source, type). Different item kinds prefix differently
   *  (`name:${docId}`, `invite:${invitationId}`) — never collide. */
  id: string;
  createdAt: string;
}

/** Additional fields items tied to a document carry — mimeType/sizeBytes
 *  let the row's View button open the shared DocumentPreview modal
 *  without a second fetch. Non-document items (invitations, future
 *  system messages) extend the plain QueueItemBase instead. */
interface DocumentQueueItemBase extends QueueItemBase {
  documentId: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface NamePendingItem extends DocumentQueueItemBase {
  type: "name_pending";
  suggestedName: string;
}

export interface TagsPendingItem extends DocumentQueueItemBase {
  type: "tags_pending";
  /** Existing catalog tags AI recommends — accepting applies the tag. */
  suggestedTags: string[];
  /** Brand-new tag names AI proposes — accepting creates the tag first. */
  proposedTags: string[];
  /** Free-text rationale the model returned, if any. Shown on hover. */
  suggestionReasoning: string | null;
}

export interface SplitCandidateItem extends DocumentQueueItemBase {
  type: "split_candidate";
  /** Model's confidence — the activity drops "low" at the source, so we
   *  only ever see "medium" or "high" here. */
  confidence: "high" | "medium";
  /** Proposed page-range → title breakdown. Fed straight into
   *  SplitDocumentModal's `initialParts` prop. */
  proposedParts: { pages: string; title?: string }[];
  reasoning: string | null;
  pageCount: number;
  /** Full metadata blob is passed to the modal so it doesn't need to
   *  refetch the document just to render (its viewer + validators want
   *  the whole `Document.metadata`). */
  documentMetadata: Record<string, unknown>;
  // NOTE: `mimeType` moved up to QueueItemBase — SplitDocumentModal still
  // branches on PDF vs non-PDF using the inherited field.
}

export interface EventPendingItem extends DocumentQueueItemBase {
  type: "event_pending";
  /** The document_events row id — needed for the review endpoint via
   *  documentId, but kept here for logging / debugging. */
  eventId: string;
  title: string;
  /** ISO datetime string from the server. Rendered as a locale string. */
  startTime: string;
  /** Optional — many events are appointment-only with no explicit end. */
  endTime: string | null;
  allDay: boolean;
  location: string | null;
  description: string | null;
}

/** An org invitation waiting on this user's yes/no. Distinct from every
 *  other item type in that it isn't tied to a document — extends the
 *  plain QueueItemBase, not DocumentQueueItemBase. Not bulk-acceptable
 *  because acceptance switches the user's active org, and doing that
 *  three times in a row from a single click is confusing UX (same
 *  rationale we used for split_candidate). */
export interface InvitationPendingItem extends QueueItemBase {
  type: "invitation_pending";
  /** invitation_tokens row id — needed for the decline endpoint. */
  invitationId: string;
  /** The JWT — passed to /api/invitations/accept. */
  token: string;
  /** Recipient email as stored on the invitation (the current user's
   *  email, but preserved verbatim for display / case-sensitivity). */
  email: string;
  orgId: string;
  orgName: string;
  role: "OWNER" | "ADMIN" | "MEMBER";
  invitedByName: string;
  expiresAt: string;
}

/** Every discriminant in the QueueItem union. Used for the filter chips. */
export const QUEUE_ACTION_TYPES = [
  "name_pending",
  "tags_pending",
  "split_candidate",
  "event_pending",
  "invitation_pending",
] as const;
export type QueueActionType = (typeof QUEUE_ACTION_TYPES)[number];

export const ACTION_TYPE_LABELS: Record<QueueActionType, string> = {
  name_pending: "Confirm name",
  tags_pending: "Confirm tags",
  split_candidate: "Review split",
  event_pending: "Confirm event",
  invitation_pending: "Accept invitation",
};
